import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Users, Video, MapPin, FileText, Pencil, Clock } from "lucide-react";
import { Input, Textarea } from "@heroui/input";
import { DatePicker } from "@heroui/date-picker";
import { useSession, useUser } from "@clerk/clerk-react";
import { parseDate, Time } from "@internationalized/date";
import { Button } from "@heroui/button";
import { addToast } from "@heroui/toast";
import { ArrowLeft, ArrowRight, CircleCheckBig } from "lucide-react";
import { getNextAvailable15MinTime, getNextAvailable15MinTimeBasedOnDuration, 
    getCurrentDate, getReadableTimeDiff, timeIntervals, formatDateToYMD} from "@/utils/dates";
import { getByShortCode as getByShortCodeApi,
    updateMeet as updateMeetApi
} from "@/api/meets";
import TabsComp from "../tabs-comp";
import InviteMembersComp from "../members/invite-members-comp";
import GooglePlacesSearchDropdown from "../maps/location-search";
import TimeWheelDrawer from "../date-time/TimeWheelDrawer";
import DatePickerLike from "../date-pickers";

const EditMeetsComp = ({short_code}) => {
const { session } = useSession();
const { user } = useUser()
const navigate = useNavigate()

const baseBtnUpdateText = 'Update'
const locationDataObj = {'name': '', 'place_id': '', 'lat': '', 'lng': ''};
const [record, setRecord] = useState(null)
const [updating, setUpdating] = useState(false);
// const [selectedDate, setSelectedDate] = useState(getCurrentDate());
const [selectedStartTime, setSelectedStartTime] = useState(getNextAvailable15MinTime());
const [selectedEndTime, setSelectedEndTime] = useState(getNextAvailable15MinTimeBasedOnDuration(1));
const [meetingDuration, setMeetingDuration] = useState(
    getReadableTimeDiff(getNextAvailable15MinTime(), getNextAvailable15MinTimeBasedOnDuration(1))
);
const [selectedMembersIds, setSelectedMembersIds] = useState([])
const [currentTab, setCurrentTab] = useState('details')
const [currentParticipantsIds, setCurrentParticipantsIds] = useState([])
const [selectedParticipants, setSelectedParticipants] = useState([])
const tabs = [
    {'key': 'details', 'title': 'Details'},
    {'key': 'participants', 'title': 'Participants'},
]

useEffect(() => {
    getByDocId()
}, [short_code])

useEffect(() => {
    if(record === null){
      return;
    }
    if (record?.start_time?.set && record?.end_time?.set) {
        setMeetingDuration(getReadableTimeDiff(record.start_time['set'], record.end_time['set']));
    }
}, [record]);

const handleClose = () => _onResp?.("close", null);

async function getByDocId(){
    const token = await session.getToken();
    getByShortCodeApi(short_code, token).then((_resp) => {
        if(_resp.data.Result === 'OK'){
            let record = _resp.data.Record;
            record['members'] = record['members'].filter(obj => obj.member_id !== user['id'])
            const part_ids = record['members'].map(obj => obj.member_id);
            setRecord(record)
            setCurrentParticipantsIds(part_ids)
        }
    }).catch((_resp) => {
        console.log(_resp)
    })
}

async function updateMeet() {
    if(currentParticipantsIds.length === 0){
        addToast({title: 'Missing Participants', color: 'primary'})
        return
    }
    setUpdating(true)
    const token = await session.getToken();
    record['members'] = currentParticipantsIds
    updateMeetApi(record, token).then((_resp) => {
        if(_resp.data.Result === 'SUCCESS'){
            addToast({title: "Meeting Updated", 'color': 'success'})
            navigate(`/meetings/view/${short_code}`)
        }else{
            addToast({title: _resp.data.Message, 'color': 'warning'})
        }
    }).catch((_resp) => {
        console.log(_resp)
    }).finally(() => {
        setUpdating(false)
    })
}

function onKeyValueChange(_key, _value){
    setRecord((prev) => ({
        ...prev,
        [_key]: _value
    }))
}

function onKeySubKeyValueChange(_key, _sub_key, _value) {
  setRecord((prev) => ({
    ...prev,
    [_key]: {
      ...prev[_key],
      [_sub_key]: _value
    }
  }));
}

function onInviteMembersCompChange(_cmd, _value){
    if(_cmd === 'remove'){
        setCurrentParticipantsIds(prev => prev.filter(item => item !== _value['id']));
        setSelectedParticipants(prev => prev.filter(item => item['id'] !== _value['id']));
    }else if(_cmd === 'add'){
        setCurrentParticipantsIds(prev => prev.includes(_value['id']) ? prev : [...prev, _value['id']]);
        setSelectedParticipants(prev => prev.includes(_value['id']) ? prev : [...prev, _value]);
    }else{
        return
    }
}

function onGooglePlacesSearchDropdownChange(_obj){
    const obj = {
        'name': _obj['name'],
        'place_id': _obj['place_id'],
        'lat': _obj['lat'],
        'lng': _obj['lng'],
    }
    onKeyValueChange('location', obj);
}

function onTimeChange(_key, _value){
    onKeyValueChange(_key, _value)
}

if(record === null){
    return <><h3>Loading...</h3></>
}

return (<>
<div className="flex flex-col gap-6 ps-4 pe-4 pt-2">
  {currentTab === "details" && (
    <div className="flex flex-col gap-6">
      {/* Title */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-neutral-600 dark:text-zinc-400">
          <Pencil className="w-5 h-5" />
          <span className="text-sm">Title</span>
        </div>
        <Input
          onChange={(e) => onKeyValueChange("title", e.target.value)}
          value={record["title"]}
          variant="bordered"
          placeholder="Add title and time"
          classNames={{
            inputWrapper: `
              bg-white border border-neutral-200 hover:bg-neutral-50 shadow-sm rounded-xl
              dark:bg-content1/70 dark:border-default-200/60 dark:hover:bg-content2/60 dark:shadow-none
            `,
            input: "text-neutral-900 placeholder:text-neutral-400 text-base dark:text-foreground",
          }}
        />
      </div>

      {/* Date & Time */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 text-neutral-600 dark:text-zinc-400">
          <CalendarDays className="w-5 h-5" />
          <span className="text-sm">Event Date</span>
        </div>
        <div className="flex flex-col gap-4">
          <DatePickerLike onChange={(value) => onKeyValueChange('meeting_date', value)} value={record['meeting_date']}/>
        </div>
      </div>

      <div className="flex flex-col gap-1 w-full">
        <div className="flex items-center gap-2 text-neutral-700 dark:text-neutral-300">
          <Clock className="w-4 h-4" />
          <span className=" text-sm">
            Duration: {meetingDuration}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <TimeWheelDrawer value={record["start_time"]['set']} onSelect={(time) => onKeySubKeyValueChange("start_time", 'set', time)}/>
          <span className="text-lg text-neutral-500 dark:text-zinc-400">–</span>
          <TimeWheelDrawer value={record["end_time"]['set']} onSelect={(time) => onKeySubKeyValueChange("end_time", 'set', time)}/>
        </div>
      </div>

      {/* Location */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-neutral-600 dark:text-zinc-400">
          <MapPin className="w-5 h-5" />
          <span className="text-sm">Location</span>
        </div>
        <GooglePlacesSearchDropdown defaultValue={record["location"]["name"]} onSelect={onGooglePlacesSearchDropdownChange}
        />
      </div>

      {/* Description */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-neutral-600 dark:text-zinc-400">
          <FileText className="w-5 h-5" />
          <span className="text-sm">Description</span>
        </div>
        <Textarea
          rows={15}
          onChange={(e) => onKeyValueChange("description", e.target.value)}
          value={record["description"]}
          placeholder="Description..."
          classNames={{
            inputWrapper: `
              bg-white border border-neutral-200 hover:bg-neutral-50 shadow-sm rounded-xl
              dark:bg-content1/70 dark:border-default-200/60 dark:hover:bg-content2/60 dark:shadow-none
            `,
            input: "text-neutral-800 placeholder:text-neutral-400 dark:text-foreground",
          }}
        />
      </div>
    </div>
  )}

  {currentTab === "participants" && (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 text-neutral-600 dark:text-zinc-400">
        <Users className="w-5 h-5" />
        <span className="text-sm">
          Participants{" "}
          {currentParticipantsIds.length > 0 ? currentParticipantsIds.length : null}
        </span>
      </div>
      <InviteMembersComp currentMembers={currentParticipantsIds} onChange={onInviteMembersCompChange}/>
    </div>
  )}
</div>

{/* Bottom actions */}
<div className="fixed left-1/2 -translate-x-1/2 bottom-[100px] z-[50] w-full ps-4 pe-4 pointer-events-none">
  <div className="flex justify-between ps-4 pe-4 pointer-events-auto">
    <div>
      <TabsComp items={tabs} selectedKey={currentTab} defaultKey="details" onChange={(e) => setCurrentTab(e)} />
    </div>
    <div>
      <Button isLoading={updating} disabled={updating} onPress={updateMeet} size="md" radius="full" color="primary" variant="solid" className="flex items-center gap-2 flex-1" >
        {updating ? (
          <>Updating...</>
        ) : (
          <>
            Update
            <CircleCheckBig size={16} />
          </>
        )}
      </Button>
    </div>
  </div>
</div>
</>)
}

export default EditMeetsComp